import { useEffect, useState } from 'react';
import { useRestaurant } from '../contexts/restaurant.context';
import { useSelector } from 'react-redux';
import { CHECKOUT_SUMMARY_UPDATED_ROUTE } from '../router/constants';
import { axiosClient } from '../libs/axios';

export const initialState = {
    subTotal: 0,
    taxFees: 0,
    tax: 0,
    mealTax: 0,
    processingFee: 0,
    convenienceFee: 0,
    deliveryFee: 0,
    surcharge: 0,
    tip: 0,
    promoDiscount: 0,
    total: 0,
    cashPayment: 0,
    deliveryDriverTip: 0,
    externalDeliveryDriverTip: 0,
    externalDeliveryFee: 0,
    externalDeliveryTax: 0,
};

export const useCartCalculate = () => {
    const [state, setState] = useState(initialState);
    const [isLoading, setIsLoading] = useState(false);

    const restaurant = useRestaurant();

    const checkout = useSelector(state => state.checkout);
    const cart = useSelector(state => state.cart.items);
    const cartGifts = useSelector(state => state.cart.giftItems);
    const updatedCart = checkout.processUpdatedData?.cart;

    useEffect(() => {
        if (!restaurant) {
            return;
        }
        
        const isUseUpdatedCart = window.location.pathname.startsWith(CHECKOUT_SUMMARY_UPDATED_ROUTE);
        
        const controller = new AbortController();

        try {
            (async () => {
                if (!checkout.paymentMethod) {
                    return;
                }
                
                setIsLoading(true);
                
                const { data } = await axiosClient.post(`/api/restaurant/${restaurant.code}/cart/calculate`, {
                    promo_code: checkout.promoCode,
                    payment_method: checkout.paymentMethod,
                    entry_method: 'online',
                    order_method: checkout.orderMethod,
                    items: isUseUpdatedCart ? updatedCart : cart,
                    giftItems: cartGifts,
                    delivery_distance: checkout.deliveryDistance || 0,
                    tip_value: checkout.tip.fix,
                    tip_rate: checkout.tip.rate,
                    delivery_driver_tip: checkout.deliveryDriverTip,
                    external_delivery: {
                        driver_tip: checkout.externalDeliveryDriverTip,
                        fee: checkout.externalDeliveryFee,
                        tax: checkout.externalDeliveryTax,
                    },
                }, { signal: controller.signal });

                const processing_fee = data.processing_fee + data.application_fee;
                const cash_payment = data.sub_total + data.tax;
                const promo_discount = data.promo_discount + data.promo_value_discount;
                const tax_fees = data.tax
                    + data.meal_tax
                    + processing_fee
                    + data.convenience_fee
                    + data.delivery_fee
                    + data.external_delivery_fee
                    + data.external_delivery_tax;

                setState({
                    subTotal: data.sub_total,
                    taxFees: tax_fees,
                    tax: data.tax,
                    mealTax: data.meal_tax,
                    processingFee: processing_fee,
                    convenienceFee: data.convenience_fee,
                    deliveryFee: data.delivery_fee,
                    surcharge: data.surcharge,
                    tip: data.tip,
                    promoDiscount: promo_discount,
                    total: data.grand_total,
                    cashPayment: cash_payment,
                    deliveryDriverTip: data.delivery_driver_tip,
                    externalDeliveryDriverTip: data.external_delivery_driver_tip,
                    externalDeliveryFee: data.external_delivery_fee,
                    externalDeliveryTax: data.external_delivery_tax,
                });
            })();
        }
        catch (e) {
            console.error(e);
        }
        finally {
            setIsLoading(false);
        }
        
        return () => {
            controller.abort();
        };
    }, [
        restaurant,
        cart,
        updatedCart,
        cartGifts,
        checkout.deliveryDistance,
        checkout.promoCode,
        checkout.paymentMethod,
        checkout.tip,
        checkout.orderMethod,
        checkout.checkoutData,
        checkout.checkoutData?.address,
        checkout.checkoutData?.address2,
        checkout.checkoutData?.city,
        checkout.checkoutData?.state,
        checkout.checkoutData?.zipCode,
        checkout.deliveryDriverTip,
        checkout.externalDeliveryDriverTip,
        checkout.externalDeliveryFee,
        checkout.externalDeliveryTax,
    ]);

    return { isLoading, ...state };
};
